<template>
  <div class="mt-24">
    <div class="flex flex-col items-center">
      <h1 class="heading-2 centered mb-16">Set an hourly rate</h1>

      <p class="body-1 centered mb-16 md:w-320">
        Sitters respond best to jobs with rates
        <span class="block md:inline">that are competitive in your area.</span>
      </p>

      <Pill v-if="averagePayRate > 0" class="w-max mb-24" color="green"
        >Parents near you typically pay ${{ averagePayRate }}/hr</Pill
      >
    </div>

    <Card class="block w-full md:w-368 md:h-212 mx-auto">
      <div v-if="retrievingRate" class="h-full flex justify-center items-center">
        <LoadingSpinner />
      </div>
      <form v-else>
        <div class="centered mb-36 heading-5 pt-24">
          <span id="pay-rate-range">{{ `$${sliderValue[0]}-${sliderValue[1]}/hr` }}</span>
        </div>
        <div class="mx-16 md:mx-24 mb-56">
          <slider :key="renderSliderKey" v-model="sliderValue" :min="10" :max="50" prefix="$" suffix=" per hour" />
        </div>
        <div class="flex flex-row-reverse">
          <RouteStepperNextButton :stepData="reviewSection" :draftData="draftData" />
        </div>
      </form>
    </Card>
  </div>
</template>

<script lang="ts">
// Components & Icons
import Card from '@/components/card/Card.vue';
import Pill from '@/components/pill/Pill.vue';
import { ReviewSection } from '@/components/route_stepper/types';
import slider from '@/components/slider/slider.vue';
import LoadingSpinner from '@/icons/LoadingSpinner.vue';

// GraphQL
import { SITTER_AVERAGE_PAY_RATE } from '@/graphql/search/queries';
import { PaymentRate } from '@/store/modules/job/types';
import { JobPostDraft } from './types';
import RouteStepperNextButton from '../../components/route_stepper/RouteStepperNextButton.vue';
import { PropType } from 'vue';

interface JobPostRateData {
  sliderValue: number[];
  averagePayRate: number;
  retrievingRate: boolean;
  renderSliderKey: number;
}

export default {
  name: 'JobPostRate',
  components: {
    Card,
    Pill,
    slider,
    LoadingSpinner,
    RouteStepperNextButton,
  },
  props: {
    draft: {
      type: Object as PropType<JobPostDraft>,
      required: true,
    },
  },
  data(): JobPostRateData {
    return {
      sliderValue: [18, 25],
      averagePayRate: 0,
      retrievingRate: true,
      renderSliderKey: 0,
    };
  },
  computed: {
    rangeText(): string {
      return `$${this.sliderValue[0]}-${this.sliderValue[1]}/hr`;
    },
    paymentRate(): PaymentRate {
      return {
        rangePaymentRate: {
          min: this.sliderValue[0] * 100, // Convert to cents
          max: this.sliderValue[1] * 100, // Convert to cents
          paymentRateType: 'hour',
        },
      };
    },
    reviewSection(): ReviewSection {
      return {
        title: 'Hourly Rate',
        content: `$${this.sliderValue[0]}-${this.sliderValue[1]}/hr`,
        editPath: this.$route.path,
      };
    },
    draftData(): JobPostDraft {
      return {
        paymentRate: this.paymentRate,
      };
    },
  },
  methods: {
    adjustSliderValueAroundAverage(averagePayRate: number) {
      this.sliderValue = averagePayRate > 0 ? [averagePayRate - 2, averagePayRate + 2] : [18, 22];
      this.rerenderSlider();
    },
    rerenderSlider() {
      this.renderSliderKey += 1;
    },
  },
  apollo: {
    sitterSearch: {
      query: SITTER_AVERAGE_PAY_RATE,
      variables(): any {
        return {
          careType: this.draft.scheduleType,
          geoPointX: this.draft.location?.x,
          geoPointY: this.draft.location?.y,
          minRate: 800,
          rate: 5000,
        };
      },
      result({ data, loading }) {
        if (!loading) {
          this.retrievingRate = false;
          const dollarConversion = parseInt(data.sitterSearch?.averagePayRate, 10) / 100.0;
          const dollarRounded = Math.round(dollarConversion);
          this.averagePayRate = dollarRounded;
          this.adjustSliderValueAroundAverage(dollarRounded);
        }
      },
      error() {
        this.retrievingRate = false;
        this.sliderValue = [18, 22];
        this.rerenderSlider();
      },
    },
  },
};
</script>
